/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface SterilityTestDetailsProps {
  onBack: () => void;
  onRecord?: () => void;
  onViewObservation?: () => void;
}

const SterilityTestDetails: React.FC<SterilityTestDetailsProps> = ({ onBack, onRecord, onViewObservation }) => {
  const [activeTab, setActiveTab] = useState<'Summary' | 'Tubes' | 'Observations' | 'Timeline' | 'Audit'>('Summary');

  const testHeader = {
    id: 'ST-2024-0812',
    productName: 'Ampicillin injection',
    batchNumber: 'LOT-2024-X45',
    method: 'Membrane Filtration',
    status: 'In Progress',
    stage: 'Day 3 Observation',
    startDate: '12-Aug-2024',
    expectedCompletion: '26-Aug-2024'
  };

  const timelineSteps = [
    { label: 'Initiated', status: 'completed', date: '12-Aug 10:45' },
    { label: 'Incubation Started', status: 'completed', date: '12-Aug 14:00' },
    { label: 'Day 3 Observation', status: 'active', date: '15-Aug (Due)' },
    { label: 'Day 7 Observation', status: 'pending', date: '19-Aug' },
    { label: 'Day 14 Observation', status: 'pending', date: '26-Aug' },
    { label: 'Completed', status: 'pending', date: '-' }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-8 pb-20 media-prep-scope">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row justify-between gap-8 my-2">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
             <button 
                onClick={onBack}
                className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#0F172A] hover:bg-slate-50 transition-all shadow-sm"
             >
                <span className="material-symbols-outlined text-xl">arrow_back</span>
             </button>
             <div>
                <h1 className="text-2xl font-black text-slate-800 tracking-tight uppercase">{testHeader.id}</h1>
                <p className="text-slate-500 font-medium text-sm flex items-center gap-2">
                  <span className="material-symbols-outlined text-xs">vaccines</span>
                  {testHeader.productName} • {testHeader.batchNumber}
                </p>
             </div>
          </div>
          
          <div className="flex flex-wrap gap-6 pl-13">
             <div className="bg-white px-4 py-2 rounded-xl border border-slate-200 shadow-sm">
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Method</p>
                <p className="text-xs font-bold text-slate-700">{testHeader.method}</p>
             </div>
             <div className="bg-white px-4 py-2 rounded-xl border border-slate-200 shadow-sm">
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Start Date</p>
                <p className="text-xs font-bold text-slate-700">{testHeader.startDate}</p>
             </div>
             <div className="bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100 shadow-sm">
                <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest">Status</p>
                <p className="text-xs font-black text-emerald-600">{testHeader.status}</p>
             </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 max-h-[40px]">
           <button 
             onClick={onRecord}
             className="flex items-center justify-center gap-2 px-6 py-3 bg-[#FF7043] text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-sm shadow-orange-500/20 active:scale-95"
           >
              <span className="material-symbols-outlined text-base">edit_document</span>
              Record Observation
           </button>
           <button className="flex items-center justify-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-600 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm">
              <span className="material-symbols-outlined text-base">download</span>
              Download Report
           </button>
           <button className="flex items-center justify-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-600 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm">
              <span className="material-symbols-outlined text-base">print</span>
              Print
           </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-8 mb-2 mt-4">
        <div className="col-span-12 lg:col-span-8 space-y-8">
          {/* Custom Tabs */}
          <div className="flex gap-1 p-1.5 bg-slate-200/50 rounded-2xl w-fit">
            {(['Summary', 'Tubes', 'Observations', 'Timeline', 'Audit'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-8 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeTab === tab 
                  ? 'bg-white text-[#FF7043] shadow-sm' 
                  : 'text-slate-500 hover:text-[#FF7043]'
                }`}
              >
                {tab === 'Summary' ? 'Test Summary' : tab === 'Tubes' ? 'Media Tube Details' : tab === 'Observations' ? 'Observation History' : tab === 'Timeline' ? 'Test Timeline' : 'Audit Trail'}
              </button>
            ))}
          </div>

          <div className="min-h-[600px]">
            <AnimatePresence mode="wait">
              {activeTab === 'Summary' && (
                <motion.div 
                  key="summary"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-8"
                >
                  <div className="grid grid-cols-2 gap-8">
                    {/* Test Configuration */}
                    <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm space-y-8">
                      <div className="flex items-center gap-3 border-b border-slate-50 pb-6">
                        <span className="material-symbols-outlined text-[#FF7043]">settings</span>
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Test Configuration</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-y-6">
                        <div>
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Test Method</p>
                          <p className="text-xs font-bold text-slate-700 mt-1">{testHeader.method}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Test Duration</p>
                          <p className="text-xs font-bold text-slate-700 mt-1">14 Days</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Incubator</p>
                          <p className="text-xs font-bold text-slate-700 mt-1">INC-B02 (Zone A)</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Temp Range</p>
                          <p className="text-xs font-bold text-slate-700 mt-1">20°C - 25°C</p>
                        </div>
                      </div>
                    </div>

                    {/* Media Utilization */}
                    <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm space-y-8">
                       <div className="flex items-center gap-3 border-b border-slate-50 pb-6">
                        <span className="material-symbols-outlined text-blue-500">inventory_2</span>
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Media Utilization</h3>
                      </div>
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">FTM Broth</p>
                            <p className="text-xs font-bold text-slate-700 mt-0.5">Fluid Thioglycollate Medium</p>
                          </div>
                          <div className="text-right">
                             <p className="text-xs font-black text-[#0F172A]">2 / 2</p>
                             <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest">Matched</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">SCDM Broth</p>
                            <p className="text-xs font-bold text-slate-700 mt-0.5">Soybean Casein Digest Medium</p>
                          </div>
                          <div className="text-right">
                             <p className="text-xs font-black text-[#0F172A]">2 / 2</p>
                             <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest">Matched</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Progress Tracker */}
                  <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm space-y-10">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-purple-500">query_stats</span>
                      <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Progress Tracker</h3>
                    </div>

                    <div className="relative pt-2 pb-8">
                      {/* Line */}
                      <div className="absolute top-6 left-0 w-full h-1 bg-slate-100 -translate-y-1/2 rounded-full overflow-hidden">
                         <div className="h-full bg-orange-500" style={{ width: '45%' }}></div>
                      </div>
                      
                      <div className="relative flex justify-between">
                         {timelineSteps.map((step, idx) => (
                           <div key={idx} className="flex flex-col items-center gap-4 relative z-10 w-32">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 border-white shadow-xl transition-all ${
                                step.status === 'completed' ? 'bg-orange-500 text-white' :
                                step.status === 'active' ? 'bg-white text-orange-500 ring-8 ring-orange-100' :
                                'bg-slate-100 text-slate-400'
                              }`}>
                                 {step.status === 'completed' ? (
                                   <span className="material-symbols-outlined text-lg font-black">check</span>
                                 ) : (
                                   <div className={`w-3 h-3 rounded-full ${step.status === 'active' ? 'bg-orange-500 animate-pulse' : 'bg-slate-400'}`} />
                                 )}
                              </div>
                              <div className="text-center px-2">
                                <p className={`text-[10px] font-black uppercase tracking-widest ${
                                  step.status === 'pending' ? 'text-slate-400' : 
                                  step.status === 'active' ? 'text-orange-600' : 
                                  'text-slate-800'
                                }`}>{step.label}</p>
                                <p className="text-[9px] font-black text-slate-400 mt-1 uppercase tracking-tight opacity-80">{step.date}</p>
                              </div>
                           </div>
                         ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'Tubes' && (
                <motion.div 
                  key="tubes"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden text-black"
                >
                   <div className="px-8 py-6 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
                      <div className="flex items-center gap-4">
                        <div className="relative w-64">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-lg">search</span>
                          <input 
                            type="text" 
                            placeholder="Search Tube ID..."
                            className="w-full bg-white border border-slate-200 rounded-xl py-2 pl-10 pr-4 text-xs font-medium outline-none focus:border-blue-500 transition-all"
                          />
                        </div>
                        <div className="flex gap-2">
                           <button className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-800 transition-all">Filter</button>
                        </div>
                      </div>
                   </div>
                   <table className="w-full border-collapse">
                      <thead>
                         <tr className="bg-slate-50/20">
                            <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Tube ID</th>
                            <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">QR Code</th>
                            <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Media Type</th>
                            <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Batch</th>
                            <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Lot #</th>
                            <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Status</th>
                         </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                         {[1, 2, 3, 4].map(i => (
                           <tr key={i} className="hover:bg-slate-50/30 transition-colors">
                              <td className="px-8 py-4">
                                <span className="text-xs font-black text-slate-700">TUBE-0{i}</span>
                              </td>
                              <td className="px-8 py-4 font-mono text-[10px] text-blue-600 font-bold">QR-{1000 + i}</td>
                              <td className="px-8 py-4 text-xs font-bold text-slate-500">{i % 2 === 0 ? 'FTM' : 'SCDM'}</td>
                              <td className="px-8 py-4 text-xs font-bold text-slate-600 tracking-tight">MB-2024-X12</td>
                              <td className="px-8 py-4 text-xs font-medium text-slate-500">L-998{i}</td>
                              <td className="px-8 py-4">
                                <span className="px-3 py-1 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full text-[9px] font-black uppercase tracking-widest">Active</span>
                              </td>
                           </tr>
                         ))}
                      </tbody>
                   </table>
                </motion.div>
              )}

              {activeTab === 'Observations' && (
                <motion.div 
                  key="observations"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-8"
                >
                   <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden mt-8">
                      <div className="px-8 py-6 border-b border-slate-50 bg-slate-50/30">
                         <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                            <span className="material-symbols-outlined text-base text-orange-500">calendar_month</span>
                            Observation Schedule
                         </h3>
                      </div>
                      <div className="overflow-x-auto">
                         <table className="w-full">
                            <thead className="bg-slate-50/50">
                            <tr>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Day</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Planned Date</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Actual Date</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Recorded By</th>
                            </tr>
                         </thead>
                         <tbody className="divide-y divide-slate-50">
                            {[
                              { day: 'Day 3', planned: '15-Aug-2024', actual: '15-Aug 09:30', status: 'Completed', user: 'P Shyam Kumar' },
                              { day: 'Day 7', planned: '19-Aug-2024', actual: '-', status: 'Pending', user: '-' },
                              { day: 'Day 14', planned: '26-Aug-2024', actual: '-', status: 'Pending', user: '-' }
                            ].map((o, idx) => (
                              <tr key={idx}>
                                 <td className="px-8 py-4 text-[11px] font-black text-slate-700">{o.day}</td>
                                 <td className="px-8 py-4 text-[11px] font-medium text-slate-500">{o.planned}</td>
                                 <td className="px-8 py-4 text-[11px] font-medium text-slate-500">{o.actual}</td>
                                 <td className="px-8 py-4">
                                    <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
                                      o.status === 'Completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'
                                    }`}>{o.status}</span>
                                 </td>
                                 <td className="px-8 py-4 text-[11px] font-bold text-slate-600 italic">{o.user}</td>
                              </tr>
                            ))}
                         </tbody>
                      </table>
                   </div>
                </div>
                  <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden mt-8">
                     <div className="px-8 py-6 border-b border-slate-50 bg-slate-50/30">
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                           <span className="material-symbols-outlined text-base text-blue-500">visibility</span>
                           Recorded Observations
                        </h3>
                     </div>
                     <div className="overflow-x-auto">
                        <table className="w-full">
                           <thead className="bg-slate-50/50">
                            <tr>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Date</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Media</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Clarity</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Growth</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Remarks</th>
                            </tr>
                         </thead>
                         <tbody className="divide-y divide-slate-50">
                            {[
                              { date: '15-Aug 09:30', media: 'FTM', clarity: 'Clear', growth: 'No', status: 'Compliant', remarks: 'All tubes clear' },
                              { date: '15-Aug 09:35', media: 'SCDM', clarity: 'Clear', growth: 'No', status: 'Compliant', remarks: 'All tubes clear' }
                            ].map((o, idx) => (
                              <tr 
                                key={idx} 
                                onClick={onViewObservation}
                                className="hover:bg-slate-50/30 transition-colors cursor-pointer group"
                              >
                                 <td className="px-8 py-4 text-[11px] font-bold text-slate-600 group-hover:text-blue-600 underline underline-offset-4 decoration-slate-200 group-hover:decoration-blue-400">{o.date}</td>
                                 <td className="px-8 py-4 text-[11px] font-black text-blue-600">{o.media}</td>
                                 <td className="px-8 py-4 text-[11px] font-medium text-slate-500">{o.clarity}</td>
                                 <td className="px-8 py-4 text-[11px] font-bold text-emerald-600">{o.growth}</td>
                                 <td className="px-8 py-4">
                                    <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[8px] font-black uppercase tracking-widest">{o.status}</span>
                                 </td>
                                 <td className="px-8 py-4 text-[11px] font-medium text-slate-400 italic">{o.remarks}</td>
                              </tr>
                            ))}
                         </tbody>
                      </table>
                   </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'Timeline' && (
                <motion.div 
                  key="timeline"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-8 space-y-8"
                >
                   <div className="space-y-12 relative before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100">
                      {[
                        { time: '12-Aug 10:45', user: 'Harish S', action: 'Test Initiated', comment: 'Cycle started for LOT-2024-X45' },
                        { time: '12-Aug 11:30', user: 'Harish S', action: 'Media Tubes Assigned', comment: '4 tubes scanned and matched' },
                        { time: '12-Aug 14:00', user: 'System', action: 'Incubation Started', comment: 'Incubator INC-B02 threshold met' },
                        { time: '15-Aug 09:30', user: 'P Shyam Kumar', action: 'Observation Recorded', comment: 'Day 3 check: Clear growth' }
                      ].map((item, idx) => (
                        <div key={idx} className="relative pl-12">
                           <div className="absolute left-0 top-1.5 w-6 h-6 rounded-full bg-white border-2 border-orange-500 flex items-center justify-center shadow-sm">
                              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                           </div>
                           <div className="space-y-1">
                              <div className="flex items-center gap-3">
                                 <p className="text-[11px] font-black text-slate-800 uppercase tracking-tight">{item.action}</p>
                                 <span className="text-[9px] font-mono text-slate-400 font-bold">{item.time}</span>
                              </div>
                              <p className="text-xs font-medium text-slate-500">{item.comment}</p>
                              <div className="flex items-center gap-1.5 pt-1">
                                 <span className="material-symbols-outlined text-[10px] text-slate-500">person</span>
                                 <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{item.user}</span>
                              </div>
                           </div>
                        </div>
                      ))}
                   </div>
                </motion.div>
              )}

              {activeTab === 'Audit' && (
                <motion.div 
                  key="audit"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden"
                >
                   <div className="overflow-x-auto">
                      <table className="w-full">
                         <thead className="bg-slate-50/50">
                          <tr>
                             <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Date & Time</th>
                             <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">User</th>
                             <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Action</th>
                             <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Old Value</th>
                             <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">New Value</th>
                          </tr>
                       </thead>
                       <tbody className="divide-y divide-slate-50">
                          {[1, 2, 3].map(i => (
                            <tr key={i}>
                               <td className="px-8 py-4 text-[11px] font-bold text-slate-600">12-Aug 10:45</td>
                               <td className="px-8 py-4 text-[11px] font-black text-slate-700">Harish S</td>
                               <td className="px-8 py-4 text-[11px] font-medium text-slate-500">Status Update</td>
                               <td className="px-8 py-4 text-[11px] font-medium text-slate-400 italic">Draft</td>
                               <td className="px-8 py-4 text-[11px] font-black text-emerald-600 uppercase">In Progress</td>
                            </tr>
                          ))}
                       </tbody>
                    </table>
                   </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Right Side Status Panel */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
           <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm space-y-8 h-full">
              <div className="flex items-center gap-3 border-b border-slate-50 pb-6">
                <span className="material-symbols-outlined text-[#0F172A]">info</span>
                <h3 className="text-xs font-black text-[#0F172A] uppercase tracking-widest">Active Status Panel</h3>
              </div>

              <div className="space-y-8">
                 <div className="space-y-2">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Current Status</p>
                    <div className="flex items-center gap-2">
                       <div className="w-2.5 h-2.5 mb-3 bg-blue-500 rounded-full animate-pulse"></div>
                       <p className="text-lg font-black text-slate-800">Incubating</p>
                    </div>
                 </div>

                 <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                       <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Current Day</p>
                       <p className="text-lg font-black text-slate-800 mt-1">Day 3</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                       <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Remaining</p>
                       <p className="text-lg font-black text-slate-800 mt-1">11d</p>
                    </div>
                 </div>

                 <div className="p-5 bg-orange-50 border border-orange-100 rounded-[2rem] space-y-4">
                    <div className="flex items-center gap-2 text-orange-600">
                       <span className="material-symbols-outlined text-lg mb-2 font-black">notification_important</span>
                       <p className="text-[10px] font-black uppercase tracking-widest">Next Activity</p>
                    </div>
                    <p className="text-xs font-black text-slate-800 leading-relaxed uppercase tracking-tighter">Record Observation for Day 3 is due today</p>
                    <button 
                      onClick={onRecord}
                      className="w-full py-3 bg-white text-orange-600 rounded-xl text-[10px] font-black uppercase tracking-widest border border-orange-200 hover:bg-orange-100 transition-all"
                    >
                       Proceed to Observation
                    </button>
                 </div>

                 <div className="space-y-4 pt-4 border-t border-slate-50">
                    <div className="flex items-center gap-3 text-emerald-500 opacity-60">
                       <span className="material-symbols-outlined text-lg mb-3">check_circle</span>
                       <p className="text-[10px] font-black uppercase tracking-widest">Sterility Maintained</p>
                    </div>
                    <div className="flex items-center gap-3 text-[#0F172A]">
                       <span className="material-symbols-outlined text-lg mb-3">history</span>
                       <p className="text-[10px] font-black uppercase tracking-widest">Last Obs: 12-Aug 14:00</p>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default SterilityTestDetails;
